
import { Component, namespace, Vue, Watch } from 'nuxt-property-decorator';
import type { Route } from 'vue-router';
import Highlighter from 'web-highlighter';
import { SidePanelMode } from '../../side-panel/SidePanelMode';

import CloseBtn from '../../side-panel/CloseBtn.vue';
import toastService, { ToastType } from '../../toast/ToastService';
import ReadAloudStore from './read-aloud.store';
import { Language, ReadingMode } from './models';
import LocalConfigStore from '@/store/localConfig.store';
import ClientSettingsStore from '@/store/clientSettings.store';
import SidePanelStore from '@/components/ui/side-panel/side-panel.store';
import { pushEvent } from '@/util/grafana-faro';
import KeyHandleEventBus from '@/store/keyHandle.eventbus';

const webHighlighterStore = namespace('webHighlighter.store');

@Component({
  components: { CloseBtn }
})
export default class ReadAloud extends Vue {
  @webHighlighterStore.Getter
  highlighter: Highlighter;

  audioCtx = undefined;
  progress = 0;
  isPlaying = false;
  isPaused = false;
  isLoading = false;

  readingDocument = false;
  readingDocumentIndex = 0;
  readingDocumentTotal = 0;
  readAloudList = [];

  previousSection = undefined;
  skipRecentlyClicked = false;
  selectedRange = {};
  leftDrawerWidthLocal: number = this.leftDrawerWidth;

  get leftDrawerIsMinified(): boolean {
    return LocalConfigStore.leftDrawerIsMinified;
  }

  get leftDrawerWidth(): number {
    return ClientSettingsStore.leftDrawerWidth;
  }

  get isPausedState(): boolean {
    return ReadAloudStore.isPaused;
  }

  get pollyExists(): boolean {
    return !(typeof window.gPolly === 'undefined');
  }

  get stopDisabled(): boolean {
    return !this.isPlaying && !this.isPaused;
  }

  get playLabel(): string {
    return this.isPlaying
      ? this.$tc('labels.generic.pause')
      : this.$tc('labels.generic.play');
  }

  get playerActive(): boolean {
    return this.isLoading || this.isPaused || this.isPlaying;
  }

  get validToCloseSidePanel(): boolean {
    return (
      SidePanelStore.rightPanelIsOpen &&
      SidePanelStore.mode === SidePanelMode.POLLY
    );
  }

  get readingMode(): ReadingMode {
    return ReadAloudStore.readingMode;
  }

  get pollySettingsChanged(): boolean {
    return ReadAloudStore.settingsChanged;
  }

  @Watch('leftDrawerWidth')
  onLeftDrawerWidthChanged(val: number): void {
    this.leftDrawerWidthLocal = val;
  }

  @Watch('readingMode')
  onReadingModeChange(mode: ReadingMode): void {
    // If the reading mode is set to Document, we init playClicked, without any selection
    if (mode === ReadingMode.Document) {
      this.playClicked();
    }

    if (mode === ReadingMode.Section) {
      if (ReadAloudStore.currentRange) {
        this.playRangeWithPolly(ReadAloudStore.currentRange);
      }
    }
  }

  @Watch('playerActive')
  activeChanged(isActive: boolean) {
    ReadAloudStore.playerCurrentlyActive = isActive;
  }

  @Watch('$route')
  onRouteChange(_newRoute: Route) {
    this.emptyPolly();
  }

  @Watch('pollySettingsChanged')
  async onPollySettingsChanged(settingsChanged: boolean): Promise<void> {
    if (settingsChanged && this.playerActive) {
      ReadAloudStore.settingsChanged = false;
      await this.stopClicked();
    }
  }

  @Watch('isPausedState')
  onIsPausedChanged() {
    if (this.isPausedState === true) {
      this.pauseClicked();
    }
  }

  mounted() {
    if (this.pollyExists) {
      ReadAloudStore.subscribeReadAloud(this.handleHighlightMenuClick);
    }
    KeyHandleEventBus.$on('escape', this.closeAndStop);
  }

  destroyed() {
    KeyHandleEventBus.$off('escape', this.closeAndStop);
    ReadAloudStore.clearSubscribers();
    this.stopClicked().then(() => this.emptyPolly());
  }

  async closeAndStop(): Promise<void> {
    ReadAloudStore.playerShowing = false;
    if (this.validToCloseSidePanel) {
      SidePanelStore.rightPanelIsOpen = false;
    }
    await this.stopClicked();
  }

  async handleHighlightMenuClick(range: Range) {
    ReadAloudStore.playerShowing = true;
    if (this.validToCloseSidePanel) {
      SidePanelStore.rightPanelIsOpen = false;
    }
    await this.playRangeWithPolly(range);
  }

  async waitAndStopContext() {
    if (this.isLoading) {
      setTimeout(this.waitAndStopContext, 16);
    } else if (this.audioCtx) {
      await this.audioCtx.stop();
      this.audioCtx = undefined;
      try {
        const localCtx = await window.gPolly.readOutLoud({});
        localCtx.stop();
      } catch (_e) {
        // We expect an error as we provide no options and text to read.
      }
      await this.toggleStoppedState();
      await this.clearSectionHighlight();
    }
  }

  async emptyPolly() {
    await this.clearSectionHighlight();
    await this.waitAndStopContext();
  }

  async clearSectionHighlight() {
    if (this.previousSection) {
      await this.previousSection.classList.remove('pollyapp-section-highlight');
    }
  }

  async playNextElement() {
    await this.clearSectionHighlight();

    if (this.readAloudList.length === 0) {
      this.readingDocument = false;
      return;
    } else {
      this.readingDocument = true;
    }

    // Only attempt to play the next element from the readAloudList if we are reading the entire doc.
    if (this.readAloudList.length > 0) {
      this.readingDocumentIndex++;

      const e = this.readAloudList.pop();

      this.applySectionHighlight(e);
      this.previousSection = e;

      const range = document.createRange();
      range.selectNodeContents(e);

      await this.playRangeWithPolly(range);
    }
  }

  async readDocument() {
    this.readingDocument = true;
    this.readingDocumentIndex = 0;
    // only select ce-frames with an id starting with c, and don't select breadcrumbs or cm groups, because they wrap items with a cid.
    const elementsSelectorList =
      ".ce-frame[id^='c']:not(.breadcrumbs):not(.ce-extendedcms-group), .page-title__header";
    const matches = document
      .getElementById('js-page-content')
      .querySelectorAll(elementsSelectorList);
    const elementsToRead = Array.from(matches);
    this.readingDocumentTotal = elementsToRead.length;

    this.readAloudList = [];

    await elementsToRead
      .reverse()
      .forEach((element) => this.readAloudList.push(element));

    await this.playNextElement();
  }

  async attemptResume() {
    // Test current range with the range that was paused
    if (
      !(
        window.getSelection().rangeCount <= 0 ||
        window.getSelection().getRangeAt(0).toString().length <= 0
      )
    ) {
      if (window.getSelection().getRangeAt(0) !== this.selectedRange) {
        await this.stopClicked();
        this.selectedRange = {};
        return false;
      }
    }
    if (this.audioCtx && this.isPaused) {
      await this.audioCtx.resume();
      await this.togglePlayState();
      return true;
    }
    return false;
  }

  async playClicked() {
    ReadAloudStore.isPaused = false;
    if (this.validToCloseSidePanel) {
      SidePanelStore.rightPanelIsOpen = false;
    }
    if (this.isLoading) {
      console.error("can't play next while loading resource");
      return;
    }

    if (await this.attemptResume()) {
      pushEvent('Polly', {
        value: 'Resume',
        type: 'Button',
        component: 'ReadAloud'
      });
      return;
    }

    // Have we selected nothing, and was there nothing? Autoplay everything.
    // rangeCount can be 1 if there is no selection...
    if (
      window.getSelection().rangeCount <= 0 ||
      window.getSelection().getRangeAt(0).toString().length <= 0
    ) {
      await this.readDocument();
      pushEvent('Polly', {
        value: 'Play',
        type: 'Page',
        component: 'ReadAloud'
      });

      return;
    }

    // User has made a selection
    const range = window.getSelection().getRangeAt(0);
    this.selectedRange = range;

    await this.playRangeWithPolly(range);
  }

  async stopClicked() {
    if (this.isPlaying || this.isPaused) {
      pushEvent('Polly', {
        value: 'Stop',
        type: 'Button',
        component: 'ReadAloud'
      });
    }
    this.readAloudList = [];
    this.readingDocument = false;
    this.selectedRange = {};
    ReadAloudStore.isPaused = false;

    // if (this.audioCtx && this.isLoading) {this.audioCtx.stop(); this.clearSectionHighlight();}

    if (this.audioCtx && (this.isPlaying || this.isPaused)) {
      await this.audioCtx.stop(); // stops playback, terminates the context and triggers onEnd
      this.toggleStoppedState();
      await this.clearSectionHighlight();
    }
  }

  async pauseClicked() {
    if (this.audioCtx && this.isPlaying) {
      this.audioCtx.pause(); // will pause the playback temporarily
      await this.togglePausedState();
      pushEvent('Polly', {
        value: 'Pause',
        type: 'Button',
        component: 'ReadAloud'
      });
    }
  }

  async skipSectionClicked() {
    this.skipRecentlyClicked = true;
    // Stopp without clearing -> callback to onEnd will trigger the next section
    if (this.audioCtx) {
      await this.audioCtx.stop(); // stops playback, terminates the context and triggers onEnd
    }
  }

  private languageFromIsoStr(lang: string): Language {
    switch (lang) {
      case 'da':
        return Language.Danish;
      case 'es':
        return Language.Spanish;
      case 'de':
        return Language.German;
      case 'en':
        return Language.English;
      case 'fr':
        return Language.French;
      case 'is':
        return Language.Icelandic;
      case 'se':
        return Language.Swedish;
      default:
        return null;
    }
  }

  /**
   * Gets the voice type using the currently set gender, for the closest language attr tag we can find near the selected range.
   * We default to the user selected voice, if there is no such tag.
   * @param range
   */
  voiceTypeForSection(range) {
    const node = range.commonAncestorContainer;
    const parentNode = node.parentElement;
    let langNode = null;
    let languageAttr = null;
    const contentLang =
      document.querySelector('#v-main[lang]').getAttribute('lang') ??
      document.querySelector('html[lang]').getAttribute('lang');

    /* IF look into the current node, to find span with lang set from RTE */
    /* ELSE look up in DOMtree for an element with a lang attribute - this is set in TYPO3 constants */
    if (node.querySelector) {
      langNode = node;
    }
    // IF there are no lang attr on node - check the parent
    else if (parentNode.querySelector) {
      langNode = parentNode;
    } else {
      langNode = document.querySelector('#v-main[lang]') ?? null;
    }
    const nodeHasOwnLang =
      langNode.querySelectorAll(':scope span[lang]')[0]?.getAttribute('lang') ??
      langNode?.getAttribute('lang') ??
      null;
    languageAttr = nodeHasOwnLang ?? contentLang ?? 'da';

    /* Fix old RTE langs */
    languageAttr = languageAttr === 'dk' ? 'da' : languageAttr;
    languageAttr = languageAttr === 'sv' ? 'se' : languageAttr;
    /* Fix old RTE langs */

    const lang = this.languageFromIsoStr(languageAttr);
    if (lang !== null) {
      const foundLang = ReadAloudStore.voiceSelections.find(
        (voice) =>
          voice.language === lang &&
          voice.gender === ReadAloudStore.voice.gender
      );
      if (foundLang) {
        return foundLang.voice;
      }
    } else {
      console.warn(
        'Language not supported (' +
          languageAttr +
          '). Default to ' +
          this.$i18n?.locale
      );
    }

    return ReadAloudStore.voice.voice;
  }

  async playRangeWithPolly(range) {
    this.audioCtx?.stop();

    ReadAloudStore.readingMode = ReadingMode.Selection;
    this.isLoading = true;

    const readingSpeed = ReadAloudStore.speed.speed;
    const voiceType = ReadAloudStore.settingsChanged
      ? ReadAloudStore.voice.voice
      : this.voiceTypeForSection(range);

    this.audioCtx = await window.gPolly.readOutLoud({
      voice: voiceType,
      range,
      speed: readingSpeed,
      karaoke: true,
      cacheForMs: 30_000,

      onKaraokeHighlight: (node: HTMLElement) => {
        node.classList.add('pollyapp-word-highlight');
        node.focus();
      },

      onKaraokeNodeHide: (node: HTMLElement) => {
        return new Promise((resolve) => {
          node.style.transition = 'background-color 0.1s linear';
          node.classList.remove('pollyapp-word-highlight');
          setTimeout(resolve, 100);
        });
      },
      onStart: () => {
        this.togglePlayState();
      }, // triggered when text starts being read out loud, useful to remove throbbers etc
      onProgress: (changedProgress) => {
        if (changedProgress < this.progress) {
          console.warn(
            'loop in progress? try next element.',
            this.progress,
            changedProgress
          );
          this.toggleStoppedState();
          this.playNextElement();
        }
        this.progress = changedProgress;
      }, // triggered as text is being read, useful for showing progressbar
      onEnd: () => {
        // console.log('end', this.readingDocument, this.progress);
        this.toggleStoppedState();
        this.playNextElement();
      }, // triggered when the text has been fully read out loud, useful for resetting state
      onCancel: () => {
        // console.log('cancel', this.readingDocument, this.progress);
        this.toggleStoppedState();

        if (this.skipRecentlyClicked) {
          this.skipRecentlyClicked = false;
          this.playNextElement();
        }
      }, // triggered if speech synthesis is cancelled, for instance if a new range is being read out loud
      onError: (reason) => {
        console.error(reason);
        this.toggleStoppedState();
      } // triggered if any errors occur while trying to read the text out loud
    });
  }

  applySectionHighlight(e) {
    e.focus();
    e.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    e.classList.add('pollyapp-section-highlight');
  }

  settingsClicked() {
    ReadAloudStore.settingsChanged = false;
    pushEvent('Polly', {
      value: 'SettingsClicked',
      type: 'Button',
      component: 'ReadAloud'
    });
    if (ReadAloudStore.playerCurrentlyActive) {
      toastService.showToast(
        this.$i18n.tc('labels.polly.context-settings-inactive'),
        ToastType.Warning
      );
    }

    if (this.validToCloseSidePanel) {
      SidePanelStore.rightPanelIsOpen = false;
    } else {
      SidePanelStore.mode = SidePanelMode.POLLY;
      SidePanelStore.rightPanelIsOpen = true;
    }
  }

  togglePausedState() {
    this.isPlaying = false;
    this.isPaused = true;
    this.isLoading = false;
  }

  togglePlayState() {
    this.isPlaying = true;
    this.isPaused = false;
    this.isLoading = false;
  }

  toggleStoppedState() {
    this.isPlaying = false;
    this.isPaused = false;
    this.isLoading = false;
    this.readingDocument = false;
    this.progress = 0;
  }
}
